/* The run list's one menu: Status, From, Group by and Sort behind one button — the Chats
   panel's filter menu, so the app filters its lists one way — with a blue dot on the button
   while a filter narrows the list, the filters in use as chips under the bar, and the way
   into comparing two runs at any width. */
import React from "react";
import { GitCompareArrows, ListFilter } from "lucide-react";

import Fold from "@/shared/components/Fold";
import { menuItem, menuLabel, menuPanel, menuRow, menuSeparator } from "@/shared/components/menuStyles";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { IconButton, RemovableChip } from "@/shared/components/ui-kit";
import { interfaceName } from "@/shared/lib/interface-catalog";
import { SOURCE_META } from "../traceModel";

import type { Run } from "@/shared/types";

export type StatusFilter = "all" | "success" | "attention" | "error" | "running";
export type Grouping = "none" | "day" | "channel";

/** What narrows and groups the run list. `from` is "all", every chat or task run
    (`source:chat`, `source:task`), or one channel (`channel:WhatsApp`). */
export interface RunFilterState {
  status: StatusFilter;
  from: string;
  group: Grouping;
}

const SOURCES = [
  { value: "source:chat", label: "Chats" },
  { value: "source:task", label: "Tasks" },
];

export const NO_FILTERS: RunFilterState = { status: "all", from: "all", group: "none" };

const STATUSES: { value: StatusFilter; label: string; dot?: string }[] = [
  { value: "all", label: "Any status" },
  { value: "success", label: "Completed", dot: "bg-[var(--success)]" },
  { value: "attention", label: "Needs attention", dot: "bg-[var(--warn)]" },
  { value: "error", label: "Failed", dot: "bg-[var(--danger)]" },
  { value: "running", label: "Running", dot: "bg-[var(--info)]" },
];

const GROUPS: { value: Grouping; label: string }[] = [
  { value: "none", label: "No grouping" },
  { value: "day", label: "Day" },
  { value: "channel", label: "Channel" },
];

/** Where a run came in: its channel ("WhatsApp", "Schedule", "API"…), else Chat or Task. */
export const channelOf = (run: Run): string => (run.recorded ? interfaceName(run.recorded.interface) : SOURCE_META[run.source].label);

/** A queued run is about to run, so Running holds it too. */
const statusIs = (run: Run, status: StatusFilter) => status === "all" || run.status === status || (status === "running" && run.status === "queued");

const fromIs = (run: Run, from: string) =>
  from === "all" || from === `source:${run.source}` || from === `channel:${channelOf(run)}`;

/** The words for a From choice: "Chats", "Tasks", or the channel's name. */
export const fromLabel = (from: string): string => SOURCES.find((s) => s.value === from)?.label ?? from.replace(/^channel:/, "");

export const passesFilters = (run: Run, f: RunFilterState): boolean => statusIs(run, f.status) && fromIs(run, f.from);

/** A filter takes runs out of the list; grouping only arranges them, so it lights no dot. */
export const isNarrowed = (f: RunFilterState): boolean => f.status !== NO_FILTERS.status || f.from !== NO_FILTERS.from;

/** Anything chosen in the menu, grouping included: what the chips show. */
const isChosen = (f: RunFilterState): boolean => isNarrowed(f) || f.group !== NO_FILTERS.group;

/** How many runs a choice would keep, at its row's end. */
function Count({ n }: { n: number }) {
  return <span className="ml-auto pl-3 font-mono text-[11.5px] tabular-nums text-[var(--text-quaternary)]">{n}</span>;
}

export function RunFilterMenu({ runs, filters, onChange, newestFirst, onSort, onCompare }: {
  runs: Run[];
  filters: RunFilterState;
  onChange: (next: RunFilterState) => void;
  newestFirst: boolean;
  onSort: (newestFirst: boolean) => void;
  /** Shows every row's box, to pick two runs. */
  onCompare: () => void;
}) {
  const narrowed = isNarrowed(filters);
  // each count is what the choice would keep, with the other filter as it is
  const fromHere = runs.filter((r) => fromIs(r, filters.from));
  const ofStatus = runs.filter((r) => statusIs(r, filters.status));
  // the channels these runs came in through, the busiest first; one in use stays listed
  const channels = [...runs.reduce((m, r) => m.set(channelOf(r), 0), new Map<string, number>()).keys()]
    .map((name) => [name, ofStatus.filter((r) => channelOf(r) === name).length] as const)
    .sort((a, b) => b[1] - a[1]);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          size="md"
          active={narrowed}
          title="Filter, group and sort"
          aria-label={`Filter, group and sort runs${narrowed ? " (filtered)" : ""}`}
          className="relative"
        >
          <ListFilter className="w-[14px] h-[14px]" strokeWidth={1.75} />
          {narrowed && <span aria-hidden="true" className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[var(--info)]" />}
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`w-[232px] ${menuPanel}`}>
        <DropdownMenuRadioGroup value={filters.status} onValueChange={(v) => onChange({ ...filters, status: v as StatusFilter })}>
          <DropdownMenuLabel className={menuLabel}>Status</DropdownMenuLabel>
          {STATUSES.map((s) => (
            <DropdownMenuRadioItem key={s.value} value={s.value} className={menuItem}>
              <span className="flex items-center gap-2 min-w-0">{s.dot && <span className={`w-1.5 h-1.5 shrink-0 rounded-full ${s.dot}`} />}{s.label}</span>
              <Count n={fromHere.filter((r) => statusIs(r, s.value)).length} />
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className={menuSeparator} />
        <DropdownMenuRadioGroup value={filters.from} onValueChange={(v) => onChange({ ...filters, from: v })}>
          <DropdownMenuLabel className={menuLabel}>From</DropdownMenuLabel>
          <DropdownMenuRadioItem value="all" className={menuItem}>
            Anywhere<Count n={ofStatus.length} />
          </DropdownMenuRadioItem>
          {SOURCES.map((s) => (
            <DropdownMenuRadioItem key={s.value} value={s.value} className={menuItem}>
              {s.label}<Count n={ofStatus.filter((r) => fromIs(r, s.value)).length} />
            </DropdownMenuRadioItem>
          ))}
          {channels.map(([name, n]) => (
            <DropdownMenuRadioItem key={name} value={`channel:${name}`} className={menuItem}>
              <span className="truncate">{name}</span><Count n={n} />
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className={menuSeparator} />
        <DropdownMenuRadioGroup value={filters.group} onValueChange={(v) => onChange({ ...filters, group: v as Grouping })}>
          <DropdownMenuLabel className={menuLabel}>Group by</DropdownMenuLabel>
          {GROUPS.map((g) => (
            <DropdownMenuRadioItem key={g.value} value={g.value} className={menuItem}>{g.label}</DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className={menuSeparator} />
        <DropdownMenuRadioGroup value={newestFirst ? "newest" : "oldest"} onValueChange={(v) => onSort(v === "newest")}>
          <DropdownMenuLabel className={menuLabel}>Sort</DropdownMenuLabel>
          <DropdownMenuRadioItem value="newest" className={menuItem}>Newest first</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="oldest" className={menuItem}>Oldest first</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className={menuSeparator} />
        <DropdownMenuItem className={menuRow} onSelect={onCompare}>
          <GitCompareArrows className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={1.75} />
          Compare two runs
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The filters in use, each with its ×, and Clear all. Folds away when there are none. */
export function ActiveFilters({ filters, onChange }: { filters: RunFilterState; onChange: (next: RunFilterState) => void }) {
  const status = STATUSES.find((s) => s.value === filters.status);
  const group = GROUPS.find((g) => g.value === filters.group);
  return (
    <Fold open={isChosen(filters)}>
      <div className="flex items-center gap-1.5 flex-wrap pb-3">
        {filters.status !== "all" && status && (
          <RemovableChip removeLabel={`Show any status, not only ${status.label.toLowerCase()}`} onRemove={() => onChange({ ...filters, status: "all" })}>
            {status.dot && <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />}
            {status.label}
          </RemovableChip>
        )}
        {filters.from !== "all" && (
          <RemovableChip removeLabel={`Show runs from anywhere, not only ${fromLabel(filters.from)}`} onRemove={() => onChange({ ...filters, from: "all" })}>
            <span className="text-[var(--text-tertiary)]">From</span> {fromLabel(filters.from)}
          </RemovableChip>
        )}
        {filters.group !== "none" && group && (
          <RemovableChip removeLabel="Stop grouping" onRemove={() => onChange({ ...filters, group: "none" })}>
            <span className="text-[var(--text-tertiary)]">By</span> {group.label.toLowerCase()}
          </RemovableChip>
        )}
        <button
          type="button"
          onClick={() => onChange(NO_FILTERS)}
          className="press h-7 px-2 rounded-[8px] text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
        >
          Clear all
        </button>
      </div>
    </Fold>
  );
}
